/**
 * @fileoverview Folder-targeted upload hook for the Documents Library.
 *
 * Replaces the retired dropzone uploader flow (`useUploadFiles` from
 * uploadstuff) with a manual one matching the Convex upload contract:
 * POST the file bytes to a generated upload URL with a Content-Type
 * header, parse `{ storageId }` from the JSON response, then save the
 * document record. Folder drops recreate their directory structure via
 * `ensureFolderPath` (one call per unique directory chain).
 */

import { useCallback, useState } from "react";
import { useConvex } from "convex/react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { DocumentCategory } from "@cvx/jobs/gatherInformation/documents/schema";
import { toast } from "sonner";
import {
  collectFilesWithPaths,
  isAcceptedFile,
  filterAcceptedFiles,
  uploadMimeType,
  type CollectedFile,
} from "./uploadHelpers";

export interface UseLibraryUploadResult {
  /**
   * Handle a drop's DataTransfer, uploading into `targetFolderId`.
   * Resolves with the documents that were created, so callers doing
   * something with the upload beyond filing it (the POD claim evidence
   * dialog attaches them to a claim) don't have to go looking for them.
   */
  handleDrop: (
    dataTransfer: DataTransfer,
    targetFolderId?: Id<"folders">,
  ) => Promise<Id<"documents">[]>;
  /** Handle files chosen via a file input, uploading into `targetFolderId`. */
  handlePickedFiles: (
    files: File[],
    targetFolderId?: Id<"folders">,
  ) => Promise<Id<"documents">[]>;
  /** Number of accepted files still uploading. */
  uploadingCount: number;
}

/**
 * Upload pipeline for the Library Finder. Folder chains are resolved
 * sequentially up front, then files upload through a bounded worker pool
 * (4 in flight at once). `uploadingCount` increments for every accepted
 * file up front and decrements as each completes (success or failure).
 */
export function useLibraryUpload(jobId: Id<"jobs">): UseLibraryUploadResult {
  const convex = useConvex();
  const [uploadingCount, setUploadingCount] = useState(0);

  const uploadBatch = useCallback(
    async (
      collected: CollectedFile[],
      targetFolderId?: Id<"folders">,
    ): Promise<Id<"documents">[]> => {
      if (collected.length === 0) return [];
      setUploadingCount((count) => count + collected.length);

      const documentIds: Id<"documents">[] = [];

      // Pre-resolve all unique directory chains sequentially — one
      // ensureFolderPath call per chain, seeded with the drop target so
      // top-level files skip the mutation. Resolving before the parallel
      // upload phase keeps folder creation deterministic (no
      // duplicate-create races between workers). A chain that fails stays
      // out of the cache; its files fail individually below.
      const folderIdByDirKey = new Map<string, Id<"folders"> | undefined>([
        ["", targetFolderId],
      ]);
      for (const { relativeDirs } of collected) {
        const dirKey = relativeDirs.join("/");
        if (folderIdByDirKey.has(dirKey)) continue;
        try {
          const folderId = await convex.mutation(
            api.jobs.gatherInformation.folders.mutations.ensureFolderPath,
            {
              jobId,
              parentFolderId: targetFolderId,
              segments: relativeDirs,
            },
          );
          folderIdByDirKey.set(dirKey, folderId);
        } catch (err) {
          console.error(`Folder creation failed for "${dirKey}":`, err);
        }
      }

      const uploadOne = async ({ file, relativeDirs }: CollectedFile) => {
        try {
          const dirKey = relativeDirs.join("/");
          if (!folderIdByDirKey.has(dirKey)) {
            throw new Error(`Target folder "${dirKey}" was not created`);
          }
          const folderId = folderIdByDirKey.get(dirKey);

          const url = await convex.mutation(
            api.jobs.gatherInformation.documents.mutations.generateUploadUrl,
            { jobId },
          );
          const response = await fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": uploadMimeType(file),
            },
            body: file,
          });
          if (!response.ok) {
            throw new Error(`Upload failed with status ${response.status}`);
          }
          const { storageId } = (await response.json()) as {
            storageId: Id<"_storage">;
          };

          const documentId = await convex.mutation(
            api.jobs.gatherInformation.documents.mutations.saveDocument,
            {
              jobId,
              storageId,
              fileName: file.name,
              fileType: uploadMimeType(file),
              fileSize: file.size,
              category: "other" as DocumentCategory,
              folderId,
            },
          );
          documentIds.push(documentId);
        } catch (err) {
          console.error(`Upload failed for "${file.name}":`, err);
        } finally {
          setUploadingCount((count) => Math.max(0, count - 1));
        }
      };

      // Bounded-concurrency worker pool: up to 4 files in flight at once,
      // each worker pulling the next file off the shared queue.
      const queue = [...collected];
      const workers = Array.from(
        { length: Math.min(4, queue.length) },
        async () => {
          for (;;) {
            const next = queue.shift();
            if (!next) return;
            await uploadOne(next);
          }
        },
      );
      await Promise.all(workers);

      const succeeded = documentIds.length;
      const failed = collected.length - succeeded;

      if (succeeded > 0) {
        toast.success(
          `${succeeded} file${succeeded > 1 ? "s" : ""} uploaded — processing started`,
        );
      }
      if (failed > 0) {
        toast.error(`${failed} file${failed > 1 ? "s" : ""} failed to upload`);
      }

      return documentIds;
    },
    [convex, jobId],
  );

  const handleDrop = useCallback(
    async (
      dataTransfer: DataTransfer,
      targetFolderId?: Id<"folders">,
    ): Promise<Id<"documents">[]> => {
      try {
        const items = dataTransfer.items;
        const hasDirectorySupport =
          items.length > 0 && typeof items[0].webkitGetAsEntry === "function";

        const { collected, errors } = hasDirectorySupport
          ? await collectFilesWithPaths(items).then((result) => ({
              collected: result.files,
              errors: result.errors,
            }))
          : {
              collected: Array.from(dataTransfer.files).map((file) => ({
                file,
                relativeDirs: [],
              })),
              errors: 0,
            };

        if (errors > 0) {
          toast.warning(
            `${errors} file${errors > 1 ? "s" : ""} could not be read and ${errors > 1 ? "were" : "was"} skipped`,
          );
        }

        const accepted = collected.filter(({ file }) => isAcceptedFile(file));
        if (accepted.length === 0) {
          if (collected.length > 0) {
            toast.error("No supported files found in the dropped items");
          }
          return [];
        }
        if (accepted.length < collected.length) {
          toast.info(
            `${accepted.length} of ${collected.length} files are supported formats`,
          );
        }

        return await uploadBatch(accepted, targetFolderId);
      } catch (err) {
        console.error("Drop handling failed:", err);
        toast.error("Something went wrong reading the dropped files");
        return [];
      }
    },
    [uploadBatch],
  );

  const handlePickedFiles = useCallback(
    async (
      files: File[],
      targetFolderId?: Id<"folders">,
    ): Promise<Id<"documents">[]> => {
      const filtered = filterAcceptedFiles(files);
      if (filtered.length < files.length) {
        toast.warning(
          `${files.length - filtered.length} file(s) skipped — unsupported type.`,
        );
      }
      if (filtered.length === 0) return [];
      return await uploadBatch(
        filtered.map((file) => ({ file, relativeDirs: [] })),
        targetFolderId,
      );
    },
    [uploadBatch],
  );

  return { handleDrop, handlePickedFiles, uploadingCount };
}
